/**
 * SegmentedPicker — always-visible single-select for quick-tweak controls.
 *
 * Use when the user tweaks options per generation (Quality, Duration, Aspect Ratio)
 * and collapsing would hurt UX. Never collapses. 2-5 options always side-by-side.
 *
 * Visual tokens: bg-black/30 border border-white/10 (unselected)
 *                bg-primary-900/30 border border-primary-500 ring-1 ring-primary-500 (selected)
 *
 * Size variants:
 *   size='md' — rich card with label + description + trailingMeta (min-h-[60px])
 *   size='sm' — tight pill with optional icon + label (min-h-[44px])
 *
 * Mobile: wraps to 2-column grid below 'sm' breakpoint for 3+ options.
 *
 * API:
 *   <SegmentedPicker
 *     options={SegmentOption[]}
 *     value={string}
 *     onChange={(v) => void}
 *     label="Quality Level"
 *     size="md"
 *     layout="equal"
 *   />
 */

import { memo, useCallback } from 'react';
import type { SegmentedPickerProps, SegmentOption } from './types';

// =============================================================================
// BADGE TONE CLASSES
// =============================================================================

const BADGE_TONE_CLASSES: Record<NonNullable<SegmentOption['badge']>['tone'], string> = {
  local: 'bg-blue-900/50 text-blue-300 border border-blue-500/30',
  '4k': 'bg-green-900/50 text-green-300 border border-green-500/30',
  recommended: 'bg-purple-900/50 text-purple-300 border border-purple-500/30',
  neutral: 'bg-white/10 text-white/70',
};

// =============================================================================
// SINGLE CARD
// =============================================================================

interface CardProps<TMeta> {
  option: SegmentOption<TMeta>;
  isSelected: boolean;
  onClick: () => void;
  size: 'sm' | 'md';
  layout: 'auto' | 'equal';
  disabled: boolean;
}

function SegmentCard<TMeta = unknown>({
  option,
  isSelected,
  onClick,
  size,
  layout,
  disabled,
}: CardProps<TMeta>) {
  const isDisabled = disabled || option.disabled === true;

  const baseClasses = [
    'relative rounded-lg border transition-all text-left',
    layout === 'equal' ? 'flex-1' : '',
    size === 'md' ? 'min-h-[60px] p-3' : 'min-h-[44px] px-3 py-2',
    isSelected
      ? 'bg-primary-900/30 border-primary-500 ring-1 ring-primary-500'
      : 'bg-black/30 border-white/10 hover:bg-white/5 hover:border-white/20',
    isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = useCallback(() => {
    if (!isDisabled) onClick();
  }, [isDisabled, onClick]);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      title={isDisabled && option.disabledReason ? option.disabledReason : undefined}
      aria-pressed={isSelected}
      className={baseClasses}
    >
      {/* Badge top-right */}
      {option.badge && (
        <span
          className={[
            'absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase',
            BADGE_TONE_CLASSES[option.badge.tone],
          ].join(' ')}
        >
          {option.badge.text}
        </span>
      )}

      {size === 'md' ? (
        /* Rich card layout */
        <div className="flex flex-col gap-0.5 pr-1">
          <div className="flex items-center gap-1.5">
            {option.iconNode && (
              <span aria-hidden="true" className="text-white/60 flex-shrink-0">
                {option.iconNode}
              </span>
            )}
            <span
              className={[
                'text-sm font-semibold',
                isSelected ? 'text-primary-200' : 'text-white',
              ].join(' ')}
            >
              {option.label}
            </span>
          </div>
          {option.description && (
            <p className="text-xs text-white/50 line-clamp-2">{option.description}</p>
          )}
          {option.trailingMeta && (
            <p className="text-xs text-primary-400 font-medium">{option.trailingMeta}</p>
          )}
        </div>
      ) : (
        /* Pill layout */
        <div className="flex items-center gap-1.5 justify-center">
          {option.iconNode && (
            <span aria-hidden="true" className="text-white/70 flex-shrink-0">
              {option.iconNode}
            </span>
          )}
          <span
            className={[
              'text-sm font-medium whitespace-nowrap',
              isSelected ? 'text-primary-200' : 'text-white/80',
            ].join(' ')}
          >
            {option.label}
          </span>
        </div>
      )}
    </button>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

function SegmentedPickerInner<TMeta = unknown>({
  options,
  value,
  onChange,
  label,
  layout = 'equal',
  size = 'md',
  className = '',
  disabled = false,
}: SegmentedPickerProps<TMeta>) {
  const handleSelect = useCallback(
    (optionValue: string) => {
      if (!disabled) onChange(optionValue);
    },
    [disabled, onChange]
  );

  // For 3+ options on narrow screens, wrap to a 2-col grid; fewer options stay horizontal.
  const wrapperClasses = [
    options.length >= 3 ? 'grid grid-cols-2 sm:flex sm:flex-row gap-2' : 'flex flex-row gap-2',
    layout === 'equal' ? 'items-stretch' : 'items-start',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="space-y-2">
      {label && (
        <label className="form-label flex items-center gap-2 text-sm font-medium text-white/70">
          {label}
        </label>
      )}

      <div className={wrapperClasses} role="group" aria-label={label}>
        {options.map((option) => (
          <SegmentCard<TMeta>
            key={option.value}
            option={option}
            isSelected={option.value === value}
            onClick={() => handleSelect(option.value)}
            size={size}
            layout={layout}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

// memo() with generic doesn't work directly — wrap with explicit cast
export const SegmentedPicker = memo(SegmentedPickerInner) as typeof SegmentedPickerInner;

export default SegmentedPicker;

export type { SegmentOption, SegmentedPickerProps } from './types';
